import type {TriggerIntegrationConfigComponentProps} from '@cohort/merchants/apps';
import DescriptionModeSelect from '@cohort/merchants/apps/common/DescriptionModeSelect';
import StepContentInput from '@cohort/merchants/apps/common/StepContentInput';
import StepTitleInput from '@cohort/merchants/apps/common/StepTitleInput';
import type {Defaults} from '@cohort/merchants/apps/common/triggers/utils';
import {
  defaultMedia,
  getDescriptionMode,
  parseDefaults,
} from '@cohort/merchants/apps/common/triggers/utils';
import StravaActivitySelectPicker from '@cohort/merchants/apps/strava/triggerIntegratons/ActivitySelectPicker';
import StravaCompleteActivityMetricsForm from '@cohort/merchants/apps/strava/triggerIntegratons/completeActivity/ActivityMetricsForm';
import LanguageSelectorInput from '@cohort/merchants/components/form/LanguageSelectorInput';
import Separator from '@cohort/merchants/components/Separator';
import TriggerIntegrationDateRangeInput from '@cohort/merchants/components/trigger-integration/TriggerIntegrationDateRangeInput';
import type {StravaCompleteActivityTriggerStruct} from '@cohort/shared/apps/strava/triggers/completeActivity';
import type {Language} from '@cohort/shared/schema/common';
import {toKilometers, toKilometersPerHour, toMinutes} from '@cohort/shared/utils/convert';
import {Fragment, useEffect, useRef} from 'react';
import {isNumber} from 'remeda';

const getDefaults = (definedLanguages: Language[]): Defaults => {
  const defaults = {
    title: {
      en: `Complete an activity`,
      fr: `Compléter une activité`,
      es: `Completar una actividad`,
      de: `Schließen Sie eine Aktivität ab`,
    },
    description: {},
  };
  return parseDefaults(defaults, definedLanguages);
};

const StravaCompleteActivityTriggerIntegrationConfigComponent: React.FC<
  TriggerIntegrationConfigComponentProps<StravaCompleteActivityTriggerStruct>
> = props => {
  const init = useRef(false);
  const {
    formReturn: {watch, reset},
    step,
    trigger,
  } = props;
  const [definedLanguages] = watch(['definedLanguages']);

  useEffect(() => {
    if (init.current) {
      return;
    }
    const defaults = getDefaults(definedLanguages);
    const hasDistance = isNumber(trigger?.triggerIntegrationConfig.minDistanceMeters);
    const hasAverageElevationGain = isNumber(
      trigger?.triggerIntegrationConfig.minAverageElevationGainMeters
    );
    const hasAverageSpeed = isNumber(
      trigger?.triggerIntegrationConfig.minAverageSpeedMetersPerSecond
    );
    const hasDuration = isNumber(trigger?.triggerIntegrationConfig.minDurationSeconds);
    const hasActivityGroups = trigger?.triggerIntegrationConfig.activityGroups?.length > 0;

    const now = new Date();
    now.setUTCHours(0, 0, 0, 0);
    const defaultStartDate = new Date(now);

    const oneWeekFromNow = new Date(now);
    oneWeekFromNow.setDate(now.getDate() + 7);
    oneWeekFromNow.setUTCHours(23, 59, 59, 999);
    const defaultEndDate = new Date(oneWeekFromNow);

    reset(formValues => ({
      ...formValues,
      defaultTitle: defaults.title,
      defaultDescription: defaults.description,
      descriptionMode: getDescriptionMode(definedLanguages, defaults, step),
      title: step?.title ?? defaults.title,
      description: step?.description ?? defaults.description,
      icon: step?.icon !== undefined ? step.icon : '🏃',
      media: step?.media ?? defaultMedia,
      trigger: {
        id: trigger?.id ?? null,
        connectionId: trigger?.connectionId ?? null,
        triggerIntegrationId: 'strava.complete-activity',
        triggerIntegrationConfig: {
          activityGroups: trigger?.triggerIntegrationConfig.activityGroups ?? undefined,
          hasActivityTypes: hasActivityGroups,
          hasDistance,
          minDistanceMeters: trigger?.triggerIntegrationConfig.minDistanceMeters ?? null,
          minDistanceKilometers: hasDistance
            ? toKilometers(trigger.triggerIntegrationConfig.minDistanceMeters)
            : null,
          hasAverageElevationGain,
          minAverageElevationGainMeters:
            trigger?.triggerIntegrationConfig.minAverageElevationGainMeters ?? null,
          hasAverageSpeed,
          minAverageSpeedMetersPerSecond:
            trigger?.triggerIntegrationConfig.minAverageSpeedMetersPerSecond ?? null,
          minAverageSpeedKilometersPerHour: hasAverageSpeed
            ? toKilometersPerHour(trigger.triggerIntegrationConfig.minAverageSpeedMetersPerSecond)
            : null,
          minDurationSeconds: trigger?.triggerIntegrationConfig.minDurationSeconds ?? null,
          minDurationMinutes: hasDuration
            ? toMinutes(trigger.triggerIntegrationConfig.minDurationSeconds)
            : null,
          hasDuration,
          activityEndDate: trigger?.triggerIntegrationConfig.activityEndDateTimestamp
            ? new Date(trigger.triggerIntegrationConfig.activityEndDateTimestamp)
            : defaultEndDate,
          activityEndDateTimestamp:
            trigger?.triggerIntegrationConfig.activityEndDateTimestamp ?? defaultEndDate.getTime(),
          activityStartDate: trigger?.triggerIntegrationConfig.activityStartDateTimestamp
            ? new Date(trigger.triggerIntegrationConfig.activityStartDateTimestamp)
            : defaultStartDate,
          activityStartDateTimestamp:
            trigger?.triggerIntegrationConfig.activityStartDateTimestamp ??
            defaultStartDate.getTime(),
        },
      },
      statementEnabled: false,
    }));

    init.current = true;
  }, [definedLanguages, reset, step, trigger]);

  if (!init.current) {
    return null;
  }

  return (
    <Fragment>
      <div>
        <div className="flex flex-col gap-4">
          <TriggerIntegrationDateRangeInput
            {...props}
            startDateName="trigger.triggerIntegrationConfig.activityStartDate"
            endDateName="trigger.triggerIntegrationConfig.activityEndDate"
            startDateTimestampName="trigger.triggerIntegrationConfig.activityStartDateTimestamp"
            endDateTimestampName="trigger.triggerIntegrationConfig.activityEndDateTimestamp"
          />

          <StravaActivitySelectPicker
            {...props}
            fieldName="trigger.triggerIntegrationConfig.activityGroups"
            hasFieldName="trigger.triggerIntegrationConfig.hasActivityTypes"
          />

          <StravaCompleteActivityMetricsForm {...props} />
        </div>
      </div>
      <DescriptionModeSelect />
      <LanguageSelectorInput />
      <StepTitleInput />
      <Separator />
      <StepContentInput />
    </Fragment>
  );
};
export default StravaCompleteActivityTriggerIntegrationConfigComponent;
